import * as preact from "preact";
import { cn } from "shared/utils";
import type { BookData } from "stores/BookStore";
import * as buttonRole from "ui/buttonRole";
import type { FormattingOptionsProps } from "views/FormattingOptions";
import {
  DictionaryPane,
  type DictionaryPaneState,
  FormattingOptionsPane,
  GuidePane,
  SmartDictionaryPane,
  type SmartDictionaryPaneState,
  WordsPane,
} from "views/ReaderSidebarPanes";

export type ReaderSidebarPageData = {
  book: BookData;
  owner?: boolean;
  hasAudioFile?: boolean;
  localMode?: boolean;
  selectedTabName?: string;
};

type Props = {
  appRouter?: any;
  dictionaryPaneState?: DictionaryPaneState;
  formattingOptionsProps?: FormattingOptionsProps;
  onOpenDictionary?: () => void;
  onSourceLanguageChange?: (languageCode: string) => void;
  onSidebarToggle?: () => void;
  onTabSelect?: (tabName: string) => void;
  pageData: ReaderSidebarPageData;
  selectedTabName?: string;
  sideBarVisible?: boolean;
  sideBarWidth: number;
  /** On phones: a sheet over the bottom of the page, rather than at the side */
  bottomSheet?: boolean;
  /** The bottom sheet's height, until it's dragged larger (px) */
  bottomSheetHeight?: number;
  smartDictionaryPaneState?: SmartDictionaryPaneState;
};

/** Of the page, until its height leaving a few lines in view is measured */
const defaultSheetHeightFraction = 0.5;
/** Above the sheet, when it's expanded to fill the page */
const expandedSheetGap = 12;
/** How far a press on the sheet's tabs may move before it drags the sheet */
const dragThreshold = 8;

type ButtonProps = {
  bottomSheet?: boolean;
  glow?: boolean;
  id?: string;
  onClick?: () => void;
  onDismissHint?: () => void;
  showHint?: boolean;
  hintTitle?: string;
  hintText?: string;
  sideBarVisible?: boolean;
};

const selectedClass = (selectedTabName: string | undefined, tab: string) =>
  selectedTabName === tab ? "selected" : "";

const ReaderSidebarButtonIcon = ({
  bottomSheet,
  id,
  onClick,
  sideBarVisible,
}: {
  /** Drawn as a panel along the bottom, rather than down the side */
  bottomSheet?: boolean;
  id?: string;
  onClick?: () => void;
  sideBarVisible?: boolean;
}) => (
  <svg
    id={id}
    class="sideBarButton clickable"
    {...buttonRole.props}
    title={sideBarVisible ? "Hide Sidebar" : "Show Sidebar"}
    aria-label={sideBarVisible ? "Hide Sidebar" : "Show Sidebar"}
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    onClick={onClick}
  >
    <path
      d="M9 2H35C38.866 2 42 5.13401 42 9V35C42 38.866 38.866 42 35 42H9C5.13401 42 2 38.866 2 35V9C2 5.13401 5.13401 2 9 2Z"
      stroke="currentColor"
      stroke-width="3"
    />
    {bottomSheet ? (
      <>
        <line
          x1="3"
          y1="27"
          x2="41"
          y2="27"
          stroke="currentColor"
          stroke-width="3"
        />
        <line
          x1="12"
          y1="34"
          x2="32"
          y2="34"
          stroke="currentColor"
          stroke-width="3"
        />
      </>
    ) : (
      <>
        <line x1="28" x2="28" y2="41" stroke="currentColor" stroke-width="3" />
        <line
          x1="32"
          y1="10"
          x2="38"
          y2="10"
          stroke="currentColor"
          stroke-width="3"
        />
        <line
          x1="32"
          y1="24"
          x2="38"
          y2="24"
          stroke="currentColor"
          stroke-width="3"
        />
        <line
          x1="32"
          y1="17"
          x2="38"
          y2="17"
          stroke="currentColor"
          stroke-width="3"
        />
      </>
    )}
  </svg>
);

export const ReaderSidebarButton = ({
  bottomSheet,
  glow,
  hintText = "Tap here to see an explanation of this word.",
  hintTitle = "Explanation",
  id = "sideBarButton",
  onClick,
  onDismissHint,
  showHint,
  sideBarVisible,
}: ButtonProps) => (
  <div id="sideBarButtonContainer" class={cn({ clickable: true, glow })}>
    <ReaderSidebarButtonIcon
      bottomSheet={bottomSheet}
      id={id}
      onClick={onClick}
      sideBarVisible={sideBarVisible}
    />

    <div
      class="tooltip arrowRight"
      style={{
        display: showHint ? "block" : "none",
        top: "-4px",
      }}
    >
      <h3>{hintTitle} &nbsp;</h3>
      <div
        class="closeDialogButton dismissSidebarHint"
        onClick={(event) => {
          event.stopPropagation();
          onDismissHint?.();
        }}
      >
        &nbsp;x&nbsp;
      </div>
      <p>{hintText}</p>
    </div>
  </div>
);

type State = {
  rendered: boolean;
  /** The bottom sheet fills the page */
  expanded: boolean;
  /** The bottom sheet's height while it's being dragged */
  dragHeight?: number;
};

class ReaderSidebarView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      rendered: Boolean(props.sideBarVisible),
      expanded: false,
    };
  }

  private sheetRef = preact.createRef<HTMLDivElement>();
  private drag?: {
    pointerId: number;
    startY: number;
    startHeight: number;
    moved: boolean;
  };

  /** A drag down from the sheet's content, once it's scrolled to the top */
  private contentDrag?: {
    startY: number;
    startHeight: number;
    dragging: boolean;
  };

  componentDidMount() {
    const sheet = this.sheetRef.current;
    sheet?.addEventListener("touchstart", this.startContentDrag, {
      passive: true,
    });
    // Not passive, so that dragging the sheet can stop its content scrolling
    sheet?.addEventListener("touchmove", this.moveContentDrag, {
      passive: false,
    });
    sheet?.addEventListener("touchend", this.endContentDrag);
    sheet?.addEventListener("touchcancel", this.endContentDrag);
  }

  componentDidUpdate(previousProps: Props) {
    if (this.props.sideBarVisible && !previousProps.sideBarVisible) {
      this.setState({ rendered: true, expanded: false });
    }
  }

  componentWillUnmount() {
    const sheet = this.sheetRef.current;
    sheet?.removeEventListener("touchstart", this.startContentDrag);
    sheet?.removeEventListener("touchmove", this.moveContentDrag);
    sheet?.removeEventListener("touchend", this.endContentDrag);
    sheet?.removeEventListener("touchcancel", this.endContentDrag);
  }

  /** Whether anything between `target` and the sheet is scrolled down */
  private scrolledDown(target: EventTarget | null) {
    for (
      let element = target as Element | null;
      element && element !== this.sheetRef.current;
      element = element.parentElement
    ) {
      if (element.scrollTop > 0) {
        return true;
      }
    }
    return false;
  }

  private startContentDrag = (event: TouchEvent) => {
    const sheet = this.sheetRef.current;
    this.contentDrag =
      this.props.bottomSheet &&
      sheet &&
      event.touches.length === 1 &&
      !(event.target as Element).closest(".titleBar") &&
      !this.scrolledDown(event.target)
        ? {
            startY: event.touches[0].clientY,
            startHeight: sheet.offsetHeight,
            dragging: false,
          }
        : undefined;
  };

  private moveContentDrag = (event: TouchEvent) => {
    const drag = this.contentDrag;
    if (!drag) {
      return;
    }
    const distance = event.touches[0].clientY - drag.startY;
    if (!drag.dragging) {
      if (Math.abs(distance) < dragThreshold) {
        return;
      }
      if (distance < 0) {
        // Scrolling the content up, not dragging the sheet
        this.contentDrag = undefined;
        return;
      }
      drag.dragging = true;
    }
    event.preventDefault();
    this.dragTo(drag.startHeight - distance);
  };

  private endContentDrag = () => {
    if (this.contentDrag?.dragging) {
      this.settleDrag();
    }
    this.contentDrag = undefined;
  };

  private sheetHeights() {
    const pageHeight = this.sheetRef.current?.parentElement?.clientHeight ?? 0;
    return {
      resting:
        this.props.bottomSheetHeight ?? pageHeight * defaultSheetHeightFraction,
      expanded: pageHeight - expandedSheetGap,
    };
  }

  private startDrag = (event: PointerEvent) => {
    if (!this.props.bottomSheet || !this.sheetRef.current) {
      return;
    }
    this.drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: this.sheetRef.current.offsetHeight,
      moved: false,
    };
  };

  private moveDrag = (event: PointerEvent) => {
    const drag = this.drag;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const distance = event.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.abs(distance) < dragThreshold) {
        return;
      }
      drag.moved = true;
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    }
    this.dragTo(drag.startHeight - distance);
  };

  private dragTo(height: number) {
    this.setState({
      dragHeight: Math.min(Math.max(height, 0), this.sheetHeights().expanded),
    });
  }

  private endDrag = () => {
    const moved = this.drag?.moved;
    this.drag = undefined;
    if (moved) {
      this.settleDrag();
    }
  };

  /** Settles the dragged sheet at its resting or full height, or closes it */
  private settleDrag() {
    const { dragHeight } = this.state;
    if (dragHeight === undefined) {
      return;
    }
    const heights = this.sheetHeights();
    if (dragHeight < heights.resting * 0.6) {
      this.props.onSidebarToggle?.();
      this.setState({ dragHeight: undefined, expanded: false });
    } else {
      this.setState({
        dragHeight: undefined,
        expanded: dragHeight > (heights.resting + heights.expanded) / 2,
      });
    }
  }

  /** The sheet's height: the rest of its style is _sideBar.scss's */
  private sheetHeight() {
    const { bottomSheetHeight } = this.props;
    const { dragHeight, expanded } = this.state;
    return dragHeight !== undefined
      ? `${dragHeight}px`
      : expanded
        ? `calc(100% - ${expandedSheetGap}px)`
        : bottomSheetHeight !== undefined
          ? `${bottomSheetHeight}px`
          : `${defaultSheetHeightFraction * 100}%`;
  }

  render() {
    const {
      appRouter,
      dictionaryPaneState,
      formattingOptionsProps,
      onOpenDictionary,
      onSourceLanguageChange,
      onSidebarToggle,
      onTabSelect,
      pageData,
      selectedTabName,
      sideBarVisible,
      sideBarWidth,
      bottomSheet,
      smartDictionaryPaneState,
    } = this.props;
    const { owner } = pageData;
    const activeTabName = selectedTabName ?? pageData.selectedTabName;
    const tabs = (
      [
        ["smartDictionary", "Explain", "Context-Aware Explanation"],
        ["dictionary", "Dictionary", "Dictionary Definitions"],
        ["guide", "Guide", "AI Reading Guide: glossary and plot summary"],
        ["editWord", "Words", "Translated Words and Phrases"],
        ["formattingOptions", "Settings", "Settings"],
      ] as const
    ).map(([tab, label, title]) => (
      <div
        key={tab}
        class={`tabHeader ${selectedClass(activeTabName, tab)}`}
        data-tab={tab}
        title={title}
        onClick={() => onTabSelect?.(tab)}
      >
        {label}
      </div>
    ));

    return (
      <div
        class={cn({
          sideBar: true,
          bottomSheet,
          open: bottomSheet && sideBarVisible,
          dragging: bottomSheet && this.state.dragHeight !== undefined,
        })}
        ref={this.sheetRef}
        style={{
          display: "block",
          right: "0",
          visibility:
            sideBarVisible || this.state.rendered ? "visible" : "hidden",
          willChange: "transform",
          ...(bottomSheet
            ? { height: this.sheetHeight() }
            : {
                transform: sideBarVisible
                  ? "translateX(0)"
                  : "translateX(100%)",
                transition: "transform 200ms",
                width: `${sideBarWidth}px`,
              }),
        }}
        onTransitionEnd={(event) => {
          // Closing transitions need the sidebar contents to remain mounted until
          // the panel finishes sliding out. Tying render directly to
          // sideBarVisible makes the contents disappear before the CSS animation.
          if (
            event.target === event.currentTarget &&
            !this.props.sideBarVisible
          ) {
            this.setState({ rendered: false });
          }
        }}
      >
        <div
          class="titleBar"
          onPointerDown={this.startDrag}
          onPointerMove={this.moveDrag}
          onPointerUp={this.endDrag}
          onPointerCancel={this.endDrag}
        >
          {bottomSheet ? (
            // As a segmented control, filling the sheet's width
            <div class="segmentedControl">{tabs}</div>
          ) : (
            tabs
          )}
          <div class="tabHeaderSpacer"></div>
          {bottomSheet && (
            <div
              class="sheetCloseButton clickable"
              title="Close"
              onClick={onSidebarToggle}
            >
              <i class="icon-remove"></i>
            </div>
          )}
          {/* Last, keeping the first tab the titleBar's first child */}
          {bottomSheet && (
            <div
              class="sheetGrabber clickable"
              title={this.state.expanded ? "Shrink" : "Expand"}
              onClick={() => this.setState({ expanded: !this.state.expanded })}
            ></div>
          )}
        </div>

        <SmartDictionaryPane
          selected={activeTabName === "smartDictionary"}
          state={smartDictionaryPaneState}
        />
        <DictionaryPane
          onOpenDictionary={onOpenDictionary}
          onSourceLanguageChange={onSourceLanguageChange}
          owner={owner}
          selected={activeTabName === "dictionary"}
          state={dictionaryPaneState}
        />
        <GuidePane selected={activeTabName === "guide"} />
        <WordsPane
          appRouter={appRouter}
          localMode={pageData.localMode}
          selected={activeTabName === "editWord"}
        />
        <FormattingOptionsPane
          formattingOptionsProps={formattingOptionsProps}
          selected={activeTabName === "formattingOptions"}
        />
      </div>
    );
  }
}

export default ReaderSidebarView;
